import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, Eye, ChevronLeft, ChevronRight, Download, Truck, CheckCircle, DollarSign, MapPin, Star, Clock, RotateCcw, XCircle, AlertCircle, AlertTriangle, Plus, Upload, MessageCircle } from 'lucide-react';
import DateFilter from '@/components/DateFilter';
import CreateDriverDialog from '@/components/admin/CreateDriverDialog';
import BulkImportDialog from '@/components/admin/BulkImportDialog';

export default function AdminDrivers() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [activeView, setActiveView] = useState('all');
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);

  // Bulk selection state
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      
      // Map activeView to backend query parameters
      if (activeView === 'active') {
        params.is_online = true;
      } else if (activeView === 'approved') {
        params.is_verified = true;
      } else if (activeView === 'pending') {
        params.bgc_status = 'pending';
      } else if (activeView === 'suspended') {
        params.is_suspended = true;
      } else if (activeView !== 'all') {
        // For any other status values, pass directly
        params.status = activeView;
      }
      
      if (dateFilter) {
        if (typeof dateFilter === 'object' && dateFilter.type === 'custom') {
          params.date_from = dateFilter.from;
          params.date_to = dateFilter.to;
        } else {
          params.date_filter = dateFilter;
        }
      }
      const res = await api.get('/admin/drivers', { params });
      setDrivers(res.data.drivers || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }, [page, search, activeView, dateFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/drivers/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats');
    }
  }, []);

  useEffect(() => { 
    fetchDrivers(); 
    fetchStats();
  }, [fetchDrivers, fetchStats]);

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/drivers/export', { 
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `drivers_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Driver data exported successfully');
    } catch (err) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const statusBadge = (status) => {
    const s = (status || 'pending').toLowerCase();
    const map = { 
      approved: 'bg-emerald-50 text-emerald-800', 
      active: 'bg-emerald-50 text-emerald-800',
      pending: 'bg-amber-50 text-amber-800', 
      rejected: 'bg-rose-50 text-rose-800', 
      suspended: 'bg-slate-100 text-slate-800',
      offline: 'bg-slate-100 text-slate-800'
    };
    return <Badge variant="outline" className={`text-xs ${map[s] || 'bg-slate-100 text-slate-800'}`}>{s}</Badge>;
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    setSelectedDrivers(selectedDrivers.length === drivers.length ? [] : drivers.map(d => d.id));
  };

  const toggleSelectDriver = (driverId) => {
    setSelectedDrivers(selectedDrivers.includes(driverId) 
      ? selectedDrivers.filter(id => id !== driverId)
      : [...selectedDrivers, driverId]
    );
  };

  const handleBulkAction = () => {
    if (!bulkAction) return toast.error('Please select an action');
    if (selectedDrivers.length === 0) return toast.error('Please select at least one driver');
    setConfirmAction({ type: bulkAction, count: selectedDrivers.length, onConfirm: executeBulkAction });
    setShowConfirmDialog(true);
  };

  const executeBulkAction = async () => {
    try {
      if (bulkAction === 'delete') {
        await Promise.all(selectedDrivers.map(id => api.delete(`/admin/drivers/${id}`)));
        toast.success(`${selectedDrivers.length} driver(s) deleted`);
      } else if (bulkAction === 'export') {
        exportDriversToCSV(selectedDrivers);
        toast.success(`${selectedDrivers.length} driver(s) exported`);
        setSelectedDrivers([]);
        setBulkAction('');
        setShowConfirmDialog(false);
        return;
      }
      setSelectedDrivers([]);
      setBulkAction('');
      setShowConfirmDialog(false);
      await fetchDrivers();
    } catch (err) {
      toast.error('Bulk action failed');
    }
  };

  const exportDriversToCSV = (driverIds) => {
    const selectedData = drivers.filter(d => driverIds.includes(d.id));
    const headers = ['Driver ID', 'Name', 'Email', 'Phone', 'Vehicle Type', 'License Plate', 'Status', 'Total Deliveries', 'Rating', 'Created At'];
    const rows = selectedData.map(d => [
      d.driver_number || 'N/A',
      `"${(d.full_name || 'N/A').replace(/"/g, '""')}"`,
      d.email || 'N/A',
      d.phone || 'N/A',
      d.vehicle_type || 'N/A',
      d.license_plate || 'N/A',
      d.status || 'N/A',
      d.total_deliveries || 0,
      d.average_rating || 0,
      new Date(d.created_at).toLocaleString()
    ]);
    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `r2yn-drivers-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        if (drivers.length > 0) {
          const allSelected = selectedDrivers.length === drivers.length;
          setSelectedDrivers(allSelected ? [] : drivers.map(d => d.id));
          toast.info(allSelected ? 'All drivers deselected' : `${drivers.length} drivers selected`);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [drivers, selectedDrivers]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Driver Management</h1>
          <p className="text-muted-foreground mt-1">Manage and monitor all delivery drivers.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-driver-button">
            <Plus className="h-4 w-4 mr-2" />
            Create Driver
          </Button>
          <Button variant="outline" onClick={() => setBulkImportOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Bulk Import
          </Button>
          <Button onClick={handleExportAll} disabled={exporting} data-testid="export-all-drivers">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export All'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-5">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('all')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Drivers</CardTitle>
              <Truck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_drivers || 0}</div>
              <p className="text-xs text-muted-foreground">All registered drivers</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('active')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.active_drivers || 0}</div>
              <p className="text-xs text-muted-foreground">Currently online</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('approved')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Approved</CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.approved_drivers || 0}</div>
              <p className="text-xs text-muted-foreground">Verified drivers</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('pending')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_drivers - (stats.approved_drivers || 0) || 0}</div>
              <p className="text-xs text-muted-foreground">Awaiting approval</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveView('suspended')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Suspended</CardTitle>
              <XCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">0</div>
              <p className="text-xs text-muted-foreground">Account suspended</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Filter Tabs */}
      <Tabs value={activeView} onValueChange={(value) => { setActiveView(value); setPage(1); }} className="w-full">
        <TabsList className="grid w-full grid-cols-5 h-auto p-1 bg-muted">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="driver-tab-all"
          >
            All Drivers
          </TabsTrigger>
          <TabsTrigger 
            value="active"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="driver-tab-active"
          >
            Active
          </TabsTrigger>
          <TabsTrigger 
            value="approved"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="driver-tab-approved"
          >
            Approved
          </TabsTrigger>
          <TabsTrigger 
            value="pending"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="driver-tab-pending"
          >
            Pending
          </TabsTrigger>
          <TabsTrigger 
            value="suspended"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm py-2"
            data-testid="driver-tab-suspended"
          >
            Suspended
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeView} className="mt-6">
          <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
                data-testid="drivers-search-input"
              />
            </div>
            <Button variant="outline" onClick={() => { setSearch(''); setDateFilter(null); setActiveView('all'); setPage(1); toast.success('Filters reset'); }}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <DateFilter 
              value={dateFilter}
              onChange={(value) => { setDateFilter(value); setPage(1); }}
              className="w-[200px]"
            />
          </div>

          {/* Bulk Actions Bar */}
          {selectedDrivers.length > 0 && (
            <Card className="border-[hsl(351,96%,31%)] bg-[hsl(351,96%,31%,0.05)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="font-medium">{selectedDrivers.length} driver(s) selected</span>
                  <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} className="border rounded px-3 py-2 text-sm">
                    <option value="">Select action</option>
                    <option value="export">Export to CSV</option>
                    <option value="delete">Delete Drivers</option>
                  </select>
                  <Button onClick={handleBulkAction} disabled={!bulkAction} size="sm">
                    {bulkAction === 'export' ? 'Export' : 'Apply'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedDrivers([])}>Clear</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="drivers-table">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-3 px-2 w-12">
                      <Checkbox checked={selectedDrivers.length === drivers.length && drivers.length > 0} onCheckedChange={toggleSelectAll} />
                    </th>
                    <th className="text-left py-3 px-2 font-medium">Driver ID</th>
                    <th className="text-left py-3 px-2 font-medium">Driver</th>
                    <th className="text-left py-3 px-2 font-medium">Email</th>
                    <th className="text-left py-3 px-2 font-medium">Phone</th>
                    <th className="text-left py-3 px-2 font-medium">Vehicle</th>
                    <th className="text-left py-3 px-2 font-medium">Rating</th>
                    <th className="text-left py-3 px-2 font-medium">Status</th>
                    <th className="text-right py-3 px-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map(driver => (
                    <tr 
                      key={driver.id} 
                      className="border-b last:border-0 hover:bg-[hsl(351,96%,31%,0.02)] hover:border-l-2 hover:border-l-[hsl(351,96%,31%,0.3)] transition-all duration-200"
                      data-testid={`driver-row-${driver.id}`}
                    >
                      <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selectedDrivers.includes(driver.id)} onCheckedChange={() => toggleSelectDriver(driver.id)} />
                      </td>
                      <td className="py-3 px-2 font-mono text-xs cursor-pointer" onClick={() => navigate(`/admin/drivers/${driver.id}`)}>
                        {driver.driver_number || 'N/A'}
                      </td>
                      <td className="py-3 px-2 cursor-pointer" onClick={() => navigate(`/admin/drivers/${driver.id}`)}>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-semibold text-primary">{driver.name?.charAt(0) || 'D'}</span>
                          </div>
                          <span className="font-medium">{driver.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-muted-foreground">{driver.email}</td>
                      <td className="py-3 px-2 text-muted-foreground">{driver.phone || 'N/A'}</td>
                      <td className="py-3 px-2">
                        <div className="text-xs">
                          <div className="font-medium">{driver.vehicle_type || 'N/A'}</div>
                          <div className="text-muted-foreground">{driver.vehicle_number || ''}</div>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          <span className="text-xs">{driver.rating?.toFixed(1) || '0.0'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex flex-col gap-1">
                          {statusBadge(driver.status)}
                          {driver.is_online && <Badge variant="outline" className="text-xs bg-green-50 text-green-800">● Online</Badge>}
                        </div>
                      </td>
                      <td className="py-3 px-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-end">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => navigate(`/admin/chat?user=${driver.id}`)}
                            data-testid={`chat-driver-${driver.id}`}
                            title="Chat with Driver"
                          >
                            <MessageCircle className="h-3.5 w-3.5 text-blue-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => navigate(`/admin/drivers/${driver.id}`)}
                            data-testid={`view-driver-${driver.id}`}
                            title="View Details"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.max(1, p - 1))} 
                  disabled={page === 1}
                  data-testid="drivers-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                  disabled={page === totalPages}
                  data-testid="drivers-next-page"
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Action
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {confirmAction?.type === 'delete' && (
              <div className="space-y-3">
                <p className="text-sm">
                  You are about to <span className="font-bold text-rose-600">permanently delete {confirmAction.count} driver(s)</span>.
                </p>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone. All driver data and history will be permanently removed.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
            <Button onClick={() => confirmAction?.onConfirm()} className={confirmAction?.type === 'delete' ? 'bg-rose-600 hover:bg-rose-700' : ''}>
              {confirmAction?.type === 'delete' ? 'Delete Drivers' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Driver Dialog */}
      <CreateDriverDialog 
        open={createDialogOpen} 
        onOpenChange={setCreateDialogOpen}
        onSuccess={fetchDrivers}
      />

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        entityType="drivers"
        onSuccess={fetchDrivers}
      />
    </div>
  );
}
